import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { Column, SortState } from '@/components/ui/DataTable';
import type { ColumnFilterApi, ColumnFilterState } from '@/shared/hooks/useColumnFilters';
import { dialog } from '@/shared/store/dialogStore';
import { usePageSizeStore } from '@/shared/store/pageSizeStore';
import { useBranchesStore } from '@/shared/store/branchesStore';
import { readListSession, writeListSession, useRoutePath, type ListSession } from './listSession';

// A 1–2 char search term forces a leading-wildcard ILIKE table scan across every
// list column for almost no selectivity, so we only hit the server at 3+ chars.
const SEARCH_MIN_CHARS = 3;
// Cap CSV export so a huge filtered set can't pull 100k+ rows into browser memory.
const EXPORT_MAX_ROWS = 25000;
// Keep in sync with server list-query.js DEFAULT_MAX_OFFSET: the deepest offset the
// server will serve. We clamp the requested page so the pager can't ask beyond it
// (avoids a deep sequential scan) — a cheap stand-in for full keyset pagination.
const MAX_OFFSET_ROWS = 10000;

// A useServerList page or facets query: [keyBase, 'list' | 'facets', params, branch],
// or [keyBase, 'list' | 'facets', endpoint, params, branch] for a list given its
// own keyBase. Its key names the branch, so a branch change refetches it by itself.
export function isBranchKeyedListQuery(queryKey: readonly unknown[]): boolean {
  if (queryKey[1] !== 'list' && queryKey[1] !== 'facets') return false;
  return queryKey.length === 4 || (queryKey.length === 5 && typeof queryKey[2] === 'string');
}

// Shared server-side list driver (see memory: server-side-list-convention).
// Fetches a server-filtered/sorted/paginated page plus per-column facets, and
// produces a `columnFilter` with the SAME shape as the client-side
// useColumnFilters — so <DataTable> + <ColumnFilterMenu> work unchanged, but the
// funnel checklists come from the WHOLE table (the /facets endpoint) and the
// selections are applied server-side via the `col_filters` param.
//
// The endpoint must return { rows, total } when paginated, accept
// q/sort/dir/page/page_size + the page's primary filter params + col_filters
// (JSON), and answer `?facets=1` with { <colKey>: string[] }.
export function useServerList<T>(opts: {
  endpoint: string;
  columns: Column<T>[];
  params: Record<string, string | number | undefined>;   // resolved primary filters
  search: string;
  sort: SortState;
  colFilters: ColumnFilterState;
  setColFilters: (s: ColumnFilterState) => void;
  // The page's own toolbar state (status chips, date range…), stored with the
  // list so the page can start from it next time (useListSessionView).
  view?: unknown;
  // The first query-key element, when it is not the endpoint's: a line log
  // ('/ar-invoices/lines') keys under its documents ('ar-invoices'), so their
  // pages' invalidateQueries({ queryKey: ['ar-invoices'] }) refetch it too.
  keyBase?: string;
}) {
  const { endpoint, columns, params, search, sort, colFilters, setColFilters, view } = opts;
  // Cache keys use the BARE entity name (no leading slash) so the detail/form
  // pages' invalidateQueries({ queryKey: ['vendors'] })-style calls actually
  // hit this list — with the '/vendors' endpoint as the key, every such
  // invalidation silently missed and edits looked stale for staleTime.
  const keyBase = endpoint.replace(/^\//, '');
  // The endpoint stays in a given keyBase's keys, so two lists sharing one
  // never share a cache entry.
  const queryKeyOf = (kind: 'list' | 'facets', ...rest: unknown[]): unknown[] =>
    (opts.keyBase ? [opts.keyBase, kind, endpoint, ...rest] : [keyBase, kind, ...rest]);
  // The route this list lives on (null outside a router): its page is kept
  // for the tab (nav-15), per list (a route's tabs each keep their own).
  const path = useRoutePath();
  // Rows-per-page is the GLOBAL per-user preference: changing it on any list
  // changes it on every list (see pageSizeStore).
  const pageSize = usePageSizeStore((s) => s.pageSize);
  const setPageSize = usePageSizeStore((s) => s.setPageSize);
  // Never let the pager request past the server's deep-page cap.
  const maxPage = Math.max(1, Math.floor(MAX_OFFSET_ROWS / pageSize) + 1);

  // Search gate (item C): treat a sub-3-char term as no search so we don't scan.
  const trimmedSearch = search.trim();
  const effectiveSearch = trimmedSearch.length > 0 && trimmedSearch.length < SEARCH_MIN_CHARS ? '' : search;
  // The branch filter (sent as X-Branch-Id) changes the result set too.
  const branch = useBranchesStore((s) => s.currentBranchId);
  // Until the branch store has loaded (after F5 it waits for GET /branches),
  // the branch is unknown: nothing is fetched, and the page is neither
  // restored nor saved yet.
  const branchReady = useBranchesStore((s) => s.hydrated);
  const sig = JSON.stringify([params, effectiveSearch, sort.col, sort.dir, colFilters, pageSize, branch]);

  // Coming back to a list reopens the page you left, as long as it is the
  // same result set (same filters, search, sort, page size and branch).
  const savedPage = (saved: ListSession | null): number | null =>
    saved && saved.sig === sig ? Math.min(Math.max(1, Math.floor(saved.page) || 1), maxPage) : null;
  const restoredPage = (): number | null => savedPage(readListSession(path, keyBase));
  // The entry as this visit found it: the visit's own writes replace the stored
  // copy, but a starred default view applied after mount (TableBookmarks) can
  // still bring back the result set the user left.
  const [savedAtMount] = useState(() => readListSession(path, keyBase));
  // Armed until the first change of the result set after the list is ready
  // (data-2), and not at all when the mount already reopened the saved page.
  const awaitingDefaultView = useRef(!(branchReady && savedPage(savedAtMount) !== null));
  // The branch the list first loaded under: a later switch is not the default
  // view landing, so it never reopens the saved page.
  const readyBranch = useRef(branchReady ? branch : undefined);
  const [page, setPageRaw] = useState(() => (branchReady ? restoredPage() ?? 1 : 1));
  const userPaged = useRef(false);
  // The branch store landing after mount (F5) is not a filter change: the saved
  // page is restored in that same render, unless the user has paged meanwhile,
  // so the first fetch (enabled from this render on) is already that page.
  const [readySeen, setReadySeen] = useState(branchReady);
  if (branchReady && !readySeen) {
    setReadySeen(true);
    const saved = userPaged.current ? null : restoredPage();
    if (saved !== null) awaitingDefaultView.current = false;
    if (saved !== null && saved !== page) setPageRaw(saved);
  }
  const setPage = (p: number) => { userPaged.current = true; setPageRaw(Math.min(Math.max(1, Math.floor(p) || 1), maxPage)); };

  const filterable = useMemo(() => columns.filter((c) => c.filterValue), [columns]);
  const colByKey = useMemo(() => new Map(filterable.map((c) => [c.key, c])), [filterable]);

  // Snap back to page 1 whenever the result set changes (any filter / search /
  // sort / funnel) or the rows-per-page preference changes, so you never sit
  // on a now-empty page. Not on mount, and not when the branch store lands
  // (F5, restored above): a restored page stays.
  const lastSig = useRef(sig);
  const wasReady = useRef(branchReady);
  useEffect(() => {
    if (!wasReady.current && branchReady) {
      wasReady.current = true;
      readyBranch.current = branch;
      lastSig.current = sig;
      return;
    }
    if (lastSig.current === sig) return;
    lastSig.current = sig;
    // The default view landing on the result set the user left reopens their
    // page (only as the first change, and not after they paged); any other
    // change goes to page 1, and the first change disarms it either way.
    const back = awaitingDefaultView.current && !userPaged.current && branch === readyBranch.current ? savedPage(savedAtMount) : null;
    awaitingDefaultView.current = false;
    setPageRaw(back ?? 1);
  }, [sig, branchReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewJson = view === undefined ? '' : JSON.stringify(view);
  useEffect(() => {
    if (!branchReady) return;
    writeListSession(path, keyBase, { page, sig, view });
  }, [path, keyBase, page, sig, viewJson, branchReady]);

  const colFiltersJson = Object.keys(colFilters).length ? JSON.stringify(colFilters) : undefined;
  const listParams = { ...params, q: effectiveSearch || undefined, sort: sort.col, dir: sort.dir, page, page_size: pageSize, col_filters: colFiltersJson };
  // The branch is in the keys (X-Branch-Id is read when the request is sent):
  // a branch landing after mount or a branch switch fetches that branch's rows
  // and never serves another branch's cached page.
  const listQ = useQuery({
    queryKey: queryKeyOf('list', listParams, branch),
    queryFn: () => api.get<{ rows: T[]; total: number }>(endpoint, listParams),
    placeholderData: keepPreviousData,
    enabled: branchReady,
  });

  // A restored page past the end (rows deleted meanwhile) moves to the last page.
  const loadedTotal = listQ.data?.total;
  useEffect(() => {
    if (loadedTotal === undefined || page <= 1) return;
    const last = Math.max(1, Math.ceil(loadedTotal / pageSize));
    if (page > last) setPageRaw(last);
  }, [loadedTotal, page, pageSize]);

  // Facets reflect the PRIMARY filters + search only (not the funnels), so each
  // funnel shows every value selectable under the current toolbar filters.
  const facetParams = { ...params, q: effectiveSearch || undefined, facets: '1' };
  const facetsQ = useQuery({
    queryKey: queryKeyOf('facets', facetParams, branch),
    queryFn: () => api.get<Record<string, string[]>>(endpoint, facetParams),
    staleTime: 30_000,
    enabled: branchReady,
  });
  const facets = facetsQ.data || {};
  // A failed load is not an empty list: DataTable shows it with Try again.
  const loadError: unknown = listQ.isError ? listQ.error : null;
  const retry = () => { void listQ.refetch(); };

  const put = (key: string, f: ColumnFilterState[string] | null) => {
    const next = { ...colFilters };
    if (f == null) delete next[key]; else next[key] = f;
    setColFilters(next);
  };

  const columnFilter: ColumnFilterApi = {
    filterableKeys: new Set(filterable.map((c) => c.key)),
    activeKeys: new Set(Object.keys(colFilters)),
    anyActive: Object.keys(colFilters).length > 0,
    typeOf: (k) => (colByKey.get(k)?.filterType ?? 'values') as 'values' | 'number' | 'date',
    distinct: (k) => facets[k] || [],
    selected: (k) => { const f = colFilters[k]; return new Set(f && f.kind === 'values' ? f.values : (facets[k] ?? [])); },
    setSelected: (k, sel) => {
      const all = facets[k] || [];
      if (sel.size === 0 || sel.size >= all.length) put(k, null);
      else put(k, { kind: 'values', values: Array.from(sel) });
    },
    condition: (k) => { const f = colFilters[k]; return f && f.kind === 'cond' ? f.cond : null; },
    setCondition: (k, cond) => { if (!cond || !cond.a) put(k, null); else put(k, { kind: 'cond', cond }); },
    clear: (k) => put(k, null),
    clearAll: () => setColFilters({}),
    fetchAll: () => fetchAll(),
    listState: { error: loadError, retry, searchActive: !!effectiveSearch.trim() },
  };

  // Fetch matching rows (ignoring the page) for CSV export — capped so a huge
  // filtered set can't OOM the tab. Warn (never silently truncate) if capped.
  const fetchAll = () => fetchAllRows<T>(endpoint, listParams);

  return {
    rows: listQ.data?.rows ?? [],
    total: listQ.data?.total ?? 0,
    // Waiting for the branch is loading too (not an empty list).
    isLoading: listQ.isLoading || !branchReady,
    isFetching: listQ.isFetching,
    error: loadError,
    refetch: retry,
    page, setPage, pageSize, setPageSize,
    columnFilter,
    fetchAll,
  };
}

// Every row of a list for CSV export, page by page: a server serves at most
// its own page size (500 on most lists, 5000 on the line logs), so the first
// page's length is the page size it honours. Stops at the total, at
// EXPORT_MAX_ROWS, at an empty page or when the server says it is capped (its
// deepest page), and warns with the rows written and the total when short.
export async function fetchAllRows<T>(endpoint: string, params: Record<string, unknown>): Promise<T[]> {
  const rows: T[] = [];
  let total: number | undefined;
  let size = EXPORT_MAX_ROWS;
  for (let page = 1; rows.length < EXPORT_MAX_ROWS; page++) {
    const r = await api.get<{ rows: T[]; total: number; capped?: boolean }>(endpoint, { ...params, page, page_size: size });
    if (typeof r.total === 'number') total = r.total;
    rows.push(...r.rows);
    if (page === 1 && r.rows.length > 0 && r.rows.length < size) size = r.rows.length;
    if (!r.rows.length || r.capped || total === undefined || rows.length >= total || r.rows.length < size) break;
  }
  const out = rows.slice(0, EXPORT_MAX_ROWS);
  if (total !== undefined && total > out.length) {
    dialog.warning(`Exported the first ${out.length.toLocaleString()} rows (of ${total.toLocaleString()}). Narrow your filters to cover the rest.`);
  }
  return out;
}
